import { Injectable, inject } from '@angular/core';
import { Firestore, collection, getDocs, query, orderBy, where, Timestamp,
         addDoc, doc, updateDoc, deleteDoc, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { Playlist } from '../interfaces/playlist.interface';

@Injectable({
  providedIn: 'root'
})
export class PlaylistService {
  private firestore = inject(Firestore);
  private playlistCollection = collection(this.firestore, 'playlists');

  /** Real-time listener: all playlists sorted by year desc, order asc */
  listenToAllPlaylists(callback: (playlists: Playlist[]) => void): Unsubscribe {
    return onSnapshot(this.playlistCollection,
      (snapshot) => {
        const all = snapshot.docs.map(d => this.mapDoc(d))
          .sort((a, b) => b.year - a.year || a.order - b.order);
        callback(all);
      }
    );
  }

  /** Real-time listener: published playlists only, sorted.
   *  Filtered server-side (where published==true) — this used to stream
   *  the entire collection, including unpublished drafts, to every
   *  visitor's browser and filter in JS. A single-field equality filter
   *  like this doesn't need a composite index, so it's a safe drop-in. */
  listenToPublishedPlaylists(callback: (playlists: Playlist[]) => void): Unsubscribe {
    const publishedQuery = query(this.playlistCollection, where('published', '==', true));
    return onSnapshot(publishedQuery,
      (snapshot) => {
        const published = snapshot.docs.map(d => this.mapDoc(d))
          .sort((a, b) => b.year - a.year || a.order - b.order);
        callback(published);
      }
    );
  }

  async addPlaylist(playlist: Omit<Playlist, 'id'>): Promise<string> {
    const data: any = { ...playlist, createdAt: Timestamp.fromDate(playlist.createdAt) };
    const docRef = await addDoc(this.playlistCollection, data);
    return docRef.id;
  }

  async updatePlaylist(id: string, updates: Partial<Playlist>): Promise<void> {
    const ref = doc(this.firestore, 'playlists', id);
    await updateDoc(ref, updates as any);
  }

  async deletePlaylist(id: string): Promise<void> {
    const ref = doc(this.firestore, 'playlists', id);
    await deleteDoc(ref);
  }

  private mapDoc(d: any): Playlist {
    const data = d.data();
    return {
      id: d.id,
      title: data['title'] || '',
      month: data['month'] || '',
      year: data['year'] || new Date().getFullYear(),
      coverImageUrl: data['coverImageUrl'] || '',
      externalLink: data['externalLink'] || '',
      platform: data['platform'] || 'spotify',
      published: data['published'] ?? false,
      featured: data['featured'] ?? false,
      order: data['order'] ?? 0,
      createdAt: data['createdAt']?.toDate() || new Date()
    } as Playlist;
  }
}
